import type { IDBPTransaction, StoreNames } from 'idb'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import type { SolaDatabase, SolaDB } from './db'
import { type StorageError, toStorageError } from './errors'

/**
 * ────────────────────────────────────────────────────────────────────────────
 *  THE RULE THAT MATTERS MOST IN THIS FILE
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  NEVER await a non-IndexedDB promise inside an open transaction.
 *
 *  IndexedDB commits a transaction as soon as the microtask queue drains with
 *  no pending requests. Awaiting a `fetch`, a `crypto.subtle` call, an OPFS
 *  write, a `setTimeout`, or any other non-IDB promise therefore COMMITS the
 *  transaction early — and every subsequent operation in the callback fails
 *  with `TransactionInactiveError`, after part of the work has already been
 *  written.
 *
 *  Compute every value the transaction needs BEFORE opening it. Pass finished
 *  data in; do no I/O inside.
 *
 *  This is the single most common source of IndexedDB data loss, and it is why
 *  migrations are synchronous by type (see migrations/index.ts).
 * ────────────────────────────────────────────────────────────────────────────
 */

export type SolaTransaction<Stores extends ArrayLike<StoreNames<SolaDB>>> = IDBPTransaction<
  SolaDB,
  Stores,
  'readwrite'
>

/**
 * Run a callback inside one atomic read-write transaction spanning every named
 * store.
 *
 * Related writes belong in a single call so they commit together or not at all.
 * On any thrown error the transaction is aborted and a typed error returned —
 * a partial write is never reported as success.
 */
export async function withTransaction<Stores extends ArrayLike<StoreNames<SolaDB>>, T>(
  db: SolaDatabase,
  stores: Stores,
  run: (tx: SolaTransaction<Stores>) => Promise<T> | T,
): Promise<Result<T, StorageError>> {
  // Opening is inside the try because it can throw on its own: a closed or
  // deleted connection raises InvalidStateError here, before any callback runs.
  // Outside the try that escaped as an exception, which broke the promise this
  // module's whole signature makes — that a storage failure arrives as a typed
  // Result rather than something a caller must remember to catch.
  let tx: SolaTransaction<Stores>
  try {
    tx = db.transaction(stores, 'readwrite')
  } catch (cause) {
    return err(toStorageError(cause))
  }

  try {
    const value = await run(tx)
    await tx.done
    return ok(value)
  } catch (cause) {
    // Aborting rejects `tx.done`. The failure is reported through the returned
    // Result, so mark that rejection handled — otherwise the same failure also
    // escapes as an unhandledrejection event.
    void tx.done.catch(() => undefined)
    try {
      tx.abort()
    } catch {
      // Already aborted or already committed; the original error is what matters.
    }
    return err(toStorageError(cause))
  }
}

/** Read-only equivalent, for multi-store reads that must see a consistent view. */
export async function withReadTransaction<Stores extends ArrayLike<StoreNames<SolaDB>>, T>(
  db: SolaDatabase,
  stores: Stores,
  run: (tx: IDBPTransaction<SolaDB, Stores, 'readonly'>) => Promise<T> | T,
): Promise<Result<T, StorageError>> {
  // Inside the try for the same reason as `withTransaction` above.
  let tx: IDBPTransaction<SolaDB, Stores, 'readonly'>
  try {
    tx = db.transaction(stores, 'readonly')
  } catch (cause) {
    return err(toStorageError(cause))
  }

  try {
    const value = await run(tx)
    await tx.done
    return ok(value)
  } catch (cause) {
    void tx.done.catch(() => undefined)
    return err(toStorageError(cause))
  }
}
